import React, { useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useTranslations from '@hooks/useTranslations';
import { IModalPostalCode } from './index';
import LocationInput from '@components/FilterBox/Stock/Components/LocationInput';
import { Redux } from 'src/redux/redux.interface';
import FilterDuck from 'src/redux/filters/filter.duck';
import { modifySortFilter } from 'src/services/filters/utils/Filters.utils';
import { SortOrder, SortType } from 'src/services/stock/models/stockItems.service.model';
import { buildParameterizedPathname } from '@utils/url.utils';
import routes from 'src/constants/routes';
import { CarJourneyType } from 'src/services';
import useCustomRouter from '@hooks/useCustomRouter';
import { addressAutocomplete } from '@utils/Address.utils';

const SECTION_ID = 'location';

export const ModalPostalCodeTemplate = ({ className, setModal, onClose }: IModalPostalCode) => {
    const { t } = useTranslations();
    const dispatch = useDispatch();
    const router = useCustomRouter();
    const filters = useSelector((state: Redux) => FilterDuck.getFilters(state));
    const geoLocation = useSelector((state: Redux) => FilterDuck.getGeoLocation(state));
    const distanceRadius = useSelector((state: Redux) => FilterDuck.getDistanceRadius(state));

    const [userInput, setUserInput] = useState(null);
    const [showRequiredError, setShowRequiredError] = useState(false);
    const [showInvalidError, setShowInvalidError] = useState(false);
    const [showNoSelectError, showSetNoSelectError] = useState(false);

    useLayoutEffect(() => {
        setModal((prevState) => ({
            ...prevState,
            onCloseModal: () =>
                prevState.onCloseModal({
                    cb: () => {
                        if (!geoLocation) {
                            router.push(buildParameterizedPathname(routes.ROOT, CarJourneyType.STOCK));
                        }
                    },
                }),
        }));
    }, [geoLocation]);

    const onSubmit = async () => {
        setShowRequiredError(false);
        setShowInvalidError(false);
        showSetNoSelectError(false);
        if (!userInput || userInput === '') {
            return setShowRequiredError(true);
        }

        if (userInput && !geoLocation) {
            const firstAddress = (await addressAutocomplete(userInput)).predictions;
            if (!firstAddress?.[0]) {
                return setShowInvalidError(true);
            }

            const firstAddressDetails = (await addressAutocomplete(userInput, firstAddress?.[0].place_id)).result;
            if (!firstAddressDetails) {
                return setShowInvalidError(true);
            }

            dispatch(
                FilterDuck.setFilters(
                    modifySortFilter(filters, SortType.PROXIMITY, SortOrder.ASC),
                    firstAddressDetails?.geometry?.location,
                    distanceRadius,
                    firstAddressDetails?.formatted_address
                )
            );
        }
    };

    return (
        <div className={className}>
            <div className="description">{t('modal.postalCode.description')}</div>
            {showNoSelectError && <div className="noSelect">{t('modal.postalCode.error.select')}</div>}
            <div
                id={SECTION_ID}
                className={`input ${showRequiredError || showRequiredError || showInvalidError ? 'hasError' : ''}`}
            >
                <LocationInput
                    setUserInput={setUserInput}
                    showOnlyInput
                    callBack={(data) => {
                        if (Boolean(Object.values(data.geoLocation).length)) {
                            onClose();
                        }
                    }}
                    isModal
                />
                <button onClick={onSubmit}>{t('modal.postalCode.button')}</button>
            </div>
            {showRequiredError && <span className="error">{t('modal.postalCode.error.required')}</span>}
            {showInvalidError && <span className="error">{t('modal.postalCode.error.invalid')}</span>}
        </div>
    );
};
